import React from "react";
import {
  FiExternalLink,
  FiGlobe,
  FiMail,
  FiMapPin,
  FiPhone,
} from "react-icons/fi";
import { FaFacebookF, FaInstagram } from "react-icons/fa";

import Reveal from "../../../components/ui/Reveal";

import { POINT_TYPES } from "../data/carteInteretsUiData";
import { cn } from "../utils/carteInteretsUtils";

function ContactLine({ icon: Icon, children }) {
  if (!children) return null;

  return (
    <div className="flex items-start gap-3 text-sm font-semibold text-slate-600 dark:text-slate-300">
      <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-2xl bg-white text-[#1277B9] shadow-sm dark:bg-slate-950">
        <Icon className="h-4 w-4" />
      </span>
      <div className="min-w-0 pt-2">{children}</div>
    </div>
  );
}

export default function CarteDetailsPanel({
  loading,
  point,
  points = [],
  onSelectPoint,
}) {
  if (loading) {
    return (
      <div className="rounded-[2rem] border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-950">
        <div className="animate-pulse space-y-4">
          <div className="h-5 w-36 rounded-full bg-slate-200 dark:bg-slate-800" />
          <div className="h-8 w-3/4 rounded-full bg-slate-200 dark:bg-slate-800" />
          <div className="h-24 rounded-3xl bg-slate-100 dark:bg-slate-900" />
          <div className="h-48 rounded-3xl bg-slate-100 dark:bg-slate-900" />
        </div>
      </div>
    );
  }

  if (!point) {
    return (
      <div className="rounded-[2rem] border border-dashed border-slate-300 bg-white p-8 text-center shadow-sm dark:border-slate-700 dark:bg-slate-950">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-3xl bg-[#1277B9]/10 text-[#1277B9] dark:bg-[#1277B9]/20 dark:text-sky-300">
          <FiMapPin className="h-7 w-7" />
        </div>

        <h2 className="mt-5 text-xl font-bold text-slate-950 dark:text-white">
          Aucun lieu sélectionné
        </h2>

        <p className="mt-2 text-sm leading-7 text-slate-600 dark:text-slate-300">
          Cliquez sur un point de la carte ou sélectionnez un lieu dans la liste.
        </p>
      </div>
    );
  }

  const type = POINT_TYPES[point.type] || POINT_TYPES.service;
  const nearby = points.filter((item) => item.id !== point.id).slice(0, 8);

  return (
    <Reveal>
      <aside className="rounded-[2rem] border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-950">
        <div className="flex items-start gap-3">
          <span
            className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl text-sm font-bold text-white shadow-sm"
            style={{ backgroundColor: type.color }}
          >
            {type.shortLabel.slice(0, 1)}
          </span>

          <div className="min-w-0">
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
              {type.singular}
              {point.category ? ` · ${point.category}` : ""}
            </p>

            <h2 className="mt-1 text-2xl font-bold leading-tight text-slate-950 dark:text-white">
              {point.title}
            </h2>
          </div>
        </div>

        {point.description && (
          <p className="mt-5 text-sm leading-7 text-slate-600 dark:text-slate-300">
            {point.description}
          </p>
        )}

        <div className="mt-5 space-y-3 rounded-3xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-900/70">
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
            Informations pratiques
          </p>

          <div className="mt-4 space-y-3">
            {point.address && (
              <ContactLine icon={FiMapPin}>
                {point.mapsHref ? (
                  <a
                    href={point.mapsHref}
                    target="_blank"
                    rel="noreferrer"
                    className="hover:text-[#1277B9] hover:underline"
                  >
                    {point.address}
                  </a>
                ) : (
                  <span>{point.address}</span>
                )}
              </ContactLine>
            )}

            {point.phone && (
              <ContactLine icon={FiPhone}>
                <a
                  href={point.phoneHref}
                  className="hover:text-[#1277B9] hover:underline"
                >
                  {point.phone}
                </a>
              </ContactLine>
            )}

            {point.emails.map((mail) => (
              <ContactLine key={mail} icon={FiMail}>
                <a
                  href={`mailto:${mail}`}
                  className="break-all text-[#1277B9] hover:underline"
                >
                  {mail}
                </a>
              </ContactLine>
            ))}

            {point.website && (
              <ContactLine icon={FiGlobe}>
                <a
                  href={point.website}
                  target="_blank"
                  rel="noreferrer"
                  className="break-all text-[#1277B9] hover:underline"
                >
                  Site internet
                </a>
              </ContactLine>
            )}

            {point.facebook && (
              <ContactLine icon={FaFacebookF}>
                <a
                  href={point.facebook}
                  target="_blank"
                  rel="noreferrer"
                  className="text-[#1277B9] hover:underline"
                >
                  Facebook
                </a>
              </ContactLine>
            )}

            {point.instagram && (
              <ContactLine icon={FaInstagram}>
                <a
                  href={point.instagram}
                  target="_blank"
                  rel="noreferrer"
                  className="text-[#1277B9] hover:underline"
                >
                  Instagram
                </a>
              </ContactLine>
            )}
          </div>
        </div>

        {point.mapsHref && (
          <a
            href={point.mapsHref}
            target="_blank"
            rel="noreferrer"
            className="mt-4 inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-2xl bg-[#1277B9] px-4 text-sm font-bold text-white shadow-sm transition hover:bg-[#0f659d]"
          >
            Ouvrir dans Google Maps
            <FiExternalLink className="h-4 w-4" />
          </a>
        )}

        {nearby.length > 0 && (
          <div className="mt-5 border-t border-slate-200 pt-5 dark:border-slate-800">
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
              Autres lieux visibles
            </p>

            <div className="mt-3 max-h-64 space-y-2 overflow-y-auto pr-1">
              {nearby.map((item) => {
                const itemType = POINT_TYPES[item.type] || POINT_TYPES.service;

                return (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => onSelectPoint(item)}
                    className={cn(
                      "w-full rounded-2xl border border-slate-200 bg-white px-3 py-3 text-left transition hover:border-[#1277B9]/50 hover:bg-sky-50 dark:border-slate-800 dark:bg-slate-950 dark:hover:bg-slate-900"
                    )}
                  >
                    <div className="flex items-start gap-2">
                      <span
                        className="mt-1 h-2.5 w-2.5 shrink-0 rounded-full"
                        style={{ backgroundColor: itemType.color }}
                      />

                      <div className="min-w-0">
                        <p className="line-clamp-1 text-sm font-bold text-slate-800 dark:text-slate-100">
                          {item.title}
                        </p>
                        <p className="mt-0.5 line-clamp-1 text-xs font-semibold text-slate-400">
                          {itemType.singular}
                          {item.category ? ` · ${item.category}` : ""}
                        </p>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </aside>
    </Reveal>
  );
}